/**
 * Which backend serves which part of the app.
 *
 * The portal is being moved from iGard to VapCore an endpoint at a time. Rather than a big
 * switch, each area is turned over on its own via `VUE_APP_VAPCORE_ENDPOINTS` — a comma-separated
 * list of the areas VapCore should serve. Anything not listed stays on iGard.
 *
 *   VUE_APP_VAPCORE_ENDPOINTS=branding,auth,policies
 *
 * That lets an environment move one area, sit with it, and roll back by editing config rather
 * than reverting code. Once every area is listed everywhere, this module and the iGard paths
 * come out together.
 */

/**
 * The areas that can be switched, and whether serving them from VapCore requires the customer to
 * have signed in through VapCore too.
 *
 * Only `branding` is exempt: it is anonymous on both sides, so it can move before sign-in does.
 * Everything else is authorised with a token the issuing backend alone recognises — an iGard
 * session token means nothing to VapCore, and vice versa.
 */
const FEATURES = {
  branding: { needsVapCoreSession: false },
  auth: { needsVapCoreSession: false },
  policies: { needsVapCoreSession: true },
  claims: { needsVapCoreSession: true },
  questions: { needsVapCoreSession: true },
  submit: { needsVapCoreSession: true },
  documents: { needsVapCoreSession: true },
};

function configuredFeatures() {
  return new Set(
    (process.env.VUE_APP_VAPCORE_ENDPOINTS || '')
      .split(',')
      .map((name) => name.trim().toLowerCase())
      .filter(Boolean)
  );
}

/**
 * Reports anything wrong with the configuration: names that match no area, and areas that need a
 * VapCore session while sign-in is still on iGard.
 *
 * That second case is the one worth catching. It does not fail at startup — it fails later, as a
 * 401 from an endpoint that looks correctly configured, because the portal is holding an iGard
 * token VapCore has no way to validate.
 */
export function configurationProblems() {
  const enabled = configuredFeatures();
  const problems = [];

  for (const name of enabled) {
    if (!FEATURES[name]) {
      problems.push(
        `VUE_APP_VAPCORE_ENDPOINTS lists "${name}", which is not a known area. Known areas: ${Object.keys(
          FEATURES
        ).join(', ')}.`
      );
    }
  }

  // Enabling an area without saying where VapCore is leaves axios with no base URL, so requests
  // go to relative paths and are answered by the app's own index.html — a 200 full of HTML,
  // which fails as a confusing parse error rather than as a missing backend.
  if (enabled.size > 0 && !process.env.VUE_APP_VAPCORE_API_BASE_URL) {
    problems.push(
      `VUE_APP_VAPCORE_ENDPOINTS enables ${[...enabled].join(', ')} on VapCore, but ` +
        'VUE_APP_VAPCORE_API_BASE_URL is not set, so there is no address to send those requests to.'
    );
  }

  if (!enabled.has('auth')) {
    const needSession = [...enabled].filter(
      (name) => FEATURES[name]?.needsVapCoreSession
    );

    if (needSession.length > 0) {
      problems.push(
        `VUE_APP_VAPCORE_ENDPOINTS enables ${needSession.join(', ')} on VapCore, but sign-in is ` +
          'still on iGard. Those endpoints will reject the iGard session token. Add "auth" to the list.'
      );
    }
  }

  return problems;
}

/** True when VapCore serves this area. */
export function useVapCoreFor(feature) {
  return configuredFeatures().has(feature);
}
